import { LegalLinks } from "@/pages/Legal";
import { Link, Navigate } from "react-router-dom";
import { ArrowRight, Orbit, Sparkles, Brain, ShieldCheck } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/AppShell";
import { useAppConfig } from "@/lib/useAppConfig";

const HERO = "/images/hero-dock-coffee.jpg";

const FEATURES = [
  { icon: Orbit, title: "Six chapters, one year", body: "Clarity, Anchor, Momentum, Radiance, Yours, New You. You move on when you're ready, not when a calendar says so." },
  { icon: Sparkles, title: "Days that fit you", body: "A 10-second energy check shapes one right-sized step. Big days, slow days and recovery days all count." },
  { icon: Brain, title: "A coach who remembers", body: "CAMRYN learns what works for you and what doesn't, and you can see and correct everything it knows." },
  { icon: ShieldCheck, title: "Private by design", body: "Choose what the AI can see. Export or permanently delete everything, any time." },
];

export default function Landing() {
  const { user } = useAuth();
  const { invite_only } = useAppConfig();
  if (user) return <Navigate to={user.onboarded ? "/today" : "/start"} replace />;
  return (
    <div className="grain min-h-screen relative overflow-hidden">
      <header className="relative z-10 max-w-6xl mx-auto px-6 h-16 mt-[env(safe-area-inset-top)] flex items-center justify-between">
        <Logo />
        <div className="flex items-center gap-2 sm:gap-4 text-sm">
          <Link to="/pricing" data-testid="landing-pricing-link" className="tap px-2 py-2 text-stone-600 hover:text-stone-900">Pricing</Link>
          <Link to="/login" data-testid="landing-login-link" className="px-4 py-2 rounded-full border border-stone-300 hover:border-stone-900 chip">Sign in</Link>
        </div>
      </header>

      <section className="relative z-10 max-w-6xl mx-auto px-6 pt-4 sm:pt-16 pb-20 grid lg:grid-cols-[1.1fr_0.9fr] gap-8 lg:gap-12 items-center">
        <div>
          <div className="eyebrow mb-5 rise">A 52-week reinvention for women</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-stone-900 rise d1">
            One year.<br /><em className="text-[#C86D51] font-normal">Your pace.</em>
          </h1>
          <p className="mt-6 text-base sm:text-lg text-stone-600 max-w-xl leading-relaxed rise d2">
            CAMRYN turns your vision for the year into a plan that adapts every day to your energy, your life and what actually works for you, with real proof of who you're becoming.
          </p>
          <div className="mt-9 flex flex-wrap items-center gap-3 rise d3">
            <Link to={invite_only ? "/waitlist" : "/register"} data-testid="landing-get-started-btn" className="btn-ink inline-flex items-center gap-2 px-7 py-3.5 rounded-full font-semibold">
              {invite_only ? "Join the waitlist" : "Start your year"} <ArrowRight className="h-4 w-4" />
            </Link>
            {invite_only && <Link to="/register" data-testid="landing-have-invite-link" className="px-6 py-3.5 rounded-full text-stone-700 hover:bg-white chip border border-stone-200">I have an invite</Link>}
          </div>
          <Link to="/privacy-promise" data-testid="landing-privacy-promise-link" className="tap mt-4 inline-flex items-center gap-1.5 text-sm text-stone-700 underline underline-offset-4 rise d3"><ShieldCheck className="h-4 w-4" />How CAMRYN protects you</Link>
          <p className="mt-5 text-xs text-stone-500 rise d4">No guaranteed results, no shame: a plan built around you. Wellness coaching, not medical advice.</p>
        </div>
        <div className="relative rise d2 order-first lg:order-none" data-testid="landing-hero-photo">
          <div className="aspect-[4/3] sm:aspect-[16/10] lg:aspect-[4/5] rounded-[1.5rem] lg:rounded-[2rem] overflow-hidden shadow-[0_30px_80px_-30px_rgba(90,62,78,0.45)]">
            <img src={HERO} alt="A woman sitting cross-legged on a lake dock with her morning coffee" className="h-full w-full object-cover object-[50%_60%]" fetchpriority="high" />
          </div>
          <div className="absolute -left-6 bottom-10 card p-4 w-64 shadow-xl hidden sm:block">
            <div className="eyebrow mb-1.5">Week 9 · Anchor</div>
            <p className="font-display text-lg leading-snug">You slept better every night you were in bed by 10:30. Tonight, the same?</p>
            <p className="text-xs text-stone-500 mt-2">Remembered from your last 3 weeks</p>
          </div>
        </div>
      </section>

      <section className="relative z-10 bg-white/70 border-y border-stone-200/70">
        <div className="max-w-6xl mx-auto px-6 py-16 grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {FEATURES.map((f, i) => (
            <div key={f.title} className={`rise d${i + 1}`}>
              <f.icon className="h-6 w-6 text-[#C86D51] mb-4" strokeWidth={1.6} />
              <h3 className="font-semibold text-stone-900 mb-1.5">{f.title}</h3>
              <p className="text-sm text-stone-600 leading-relaxed">{f.body}</p>
            </div>
          ))}
        </div>
      </section>

      <footer className="relative z-10 max-w-6xl mx-auto px-6 py-10 text-xs text-stone-500 flex flex-wrap gap-4 justify-between">
        <span>© CAMRYN. Not a medical device. In an emergency, call your local emergency number.</span>
        <span className="inline-flex flex-wrap gap-x-4 gap-y-1"><Link to="/privacy-promise" data-testid="footer-privacy-promise-link" className="tap hover:underline">How CAMRYN protects you</Link><LegalLinks /></span>
      </footer>
    </div>
  );
}
